import type { Fix } from '~/lib/geolocation';
import type { RuntimeEvent } from '~/lib/event-log';
import type { HeatLogRow } from '~/lib/heat-log';
import type { EconLogRow } from '~/lib/econ-log';

const FIX_HEADER =
  'timestamp_iso,timestamp_unix_ms,lat,lng,accuracy_m,speed_mps,heading_deg,is_simulated,is_settled';

const EVENT_HEADER = 'timestamp_iso,timestamp_unix_ms,event_type,payload_json';

const HEAT_HEADER =
  'timestamp_iso,timestamp_unix_ms,heat_value,event_type,gain_amount,gain_band,origin_lat,origin_lng,target_tier,player_lat,player_lng,active_event_count,is_simulated';

const ECON_HEADER =
  'timestamp_iso,timestamp_unix_ms,event_type,amount,balance_after,detail,band,target_tier,heat_cleared,node_id,is_dual,node_price_mult,node_locked_until_unix_ms,owned_tool_count,player_lat,player_lng,is_simulated';

function field(v: number | string | null): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  // Quote any value that contains commas, quotes, or newlines.
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

/** Render a fix log to a CSV string suitable for a file download.
 *  `is_settled` is appended at the end of each row so previously-captured
 *  CSVs remain column-positionally parseable up to the prior format. */
export function fixesToCsv(fixes: Fix[]): string {
  const rows = fixes.map((f) =>
    [
      field(new Date(f.timestamp).toISOString()),
      field(f.timestamp),
      field(f.lat),
      field(f.lng),
      field(f.accuracy),
      field(f.speed),
      field(f.heading),
      field(f.simulated ? 'true' : 'false'),
      field(f.settled ? 'true' : 'false'),
    ].join(','),
  );
  return [FIX_HEADER, ...rows].join('\n') + '\n';
}

/** Render a runtime event log to a CSV string. payload_json is the
 *  JSON.stringify of the event's payload object; an empty payload is
 *  serialized as `{}`. */
export function eventsToCsv(events: RuntimeEvent[]): string {
  const rows = events.map((e) =>
    [
      field(new Date(e.timestamp).toISOString()),
      field(e.timestamp),
      field(e.type),
      field(JSON.stringify(e.payload ?? {})),
    ].join(','),
  );
  return [EVENT_HEADER, ...rows].join('\n') + '\n';
}

/** Render the heat log to a CSV string. Gain-only columns (gain_amount,
 *  gain_band, origin_*, target_tier) are blank on sample rows; the heat value
 *  is rounded to 2 decimals. From this you can reconstruct, per outcome, how
 *  much heat was added and how fast it decayed relative to player↔origin
 *  distance (player_* vs origin_* over time). */
export function heatRowsToCsv(rows: HeatLogRow[]): string {
  const out = rows.map((r) =>
    [
      field(new Date(r.timestamp).toISOString()),
      field(r.timestamp),
      field(Math.round(r.heatValue * 100) / 100),
      field(r.eventType),
      field(r.gainAmount === null ? null : Math.round(r.gainAmount * 100) / 100),
      field(r.gainBand),
      field(r.originLat),
      field(r.originLng),
      field(r.targetTier),
      field(r.playerLat),
      field(r.playerLng),
      field(r.activeEventCount),
      field(r.isSimulated ? 'true' : 'false'),
    ].join(','),
  );
  return [HEAT_HEADER, ...out].join('\n') + '\n';
}

/** Render the economy log to a CSV string. Node columns (is_dual,
 *  node_price_mult, node_locked_until_unix_ms) are blank on the balance-only
 *  sample rows and populated on event/dual-sample rows; amount is blank on
 *  state/sample rows. From this you can reconstruct median/mean ¤ payout per
 *  tier (earn rows by target_tier), earned vs spent over a session, hacks to
 *  afford a tool (owned_tool_count/balance progression), and the dual price
 *  multiplier's rise on success and decay over time (node_price_mult across
 *  dual_success and sample rows). Multiplier rounded to 3 decimals. */
export function econRowsToCsv(rows: EconLogRow[]): string {
  const out = rows.map((r) =>
    [
      field(new Date(r.timestamp).toISOString()),
      field(r.timestamp),
      field(r.eventType),
      field(r.amount),
      field(r.balanceAfter),
      field(r.detail),
      field(r.band),
      field(r.targetTier),
      field(r.heatCleared),
      field(r.nodeId),
      field(r.isDual === null ? null : r.isDual ? 'true' : 'false'),
      field(r.nodePriceMult === null ? null : Math.round(r.nodePriceMult * 1000) / 1000),
      field(r.nodeLockedUntil),
      field(r.ownedToolCount),
      field(r.playerLat),
      field(r.playerLng),
      field(r.isSimulated ? 'true' : 'false'),
    ].join(','),
  );
  return [ECON_HEADER, ...out].join('\n') + '\n';
}

/** Trigger a browser download of the supplied CSV blob.
 *  No-op in non-DOM environments. */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
